import { useId } from "react";
import { useTranslation } from "react-i18next";
import { useBoundField } from "../field-state";

const THEME_COLOR = /^theme[\s_-]*color$/i;
/** 裸十六进制（不带 `#`）在历史数据里出现过，取色器按 CSS 颜色解析，得先补上。 */
const BARE_HEX = /^(?:[a-f\d]{3}|[a-f\d]{6}|[a-f\d]{8})$/i;

function isColor(value: string) {
  return BARE_HEX.test(value) || /^(?:#|rgb|hsl)/i.test(value);
}

function toCss(value: string) {
  return BARE_HEX.test(value) ? `#${value}` : value;
}

/**
 * 取色行 + 取色浮层。行本身就是触发器，点一下开浮层；浮层里是取色器（饱和度
 * 方块 + 色相条 + 透明度条）和底部一行十六进制输入框。
 *
 * 空值和主题色哨兵都表示主题色，不用传哨兵。`defaultColor` 是「预设色号」：接口没
 * 返回颜色时按它显示，左下角恢复按钮也提交它 —— 有预设的行「默认」就是那个色号，
 * 不是主题色。没预设的行才恢复成主题色（空值）。
 */
export function ColorField({
  label,
  disabled,
  defaultColor,
}: {
  label: string;
  disabled: boolean;
  /** 预设色号，只收具体颜色。 */
  defaultColor?: string | undefined;
}) {
  const { t } = useTranslation();
  const { field, error, dismissError, handleBlur } = useBoundField<string>();
  const popoverId = useId().replace(/:/g, "");

  const stored = field.state.value.trim();
  const themed = !stored || THEME_COLOR.test(stored);
  const preset = defaultColor && isColor(defaultColor) ? toCss(defaultColor) : undefined;
  // 主题色且没有预设色号时才是真的「主题色」；有预设就按预设色号显示。
  const themeOnly = themed && !preset;
  // 取色器要有个能解析的颜色垫着。
  const value = themed ? (preset ?? "#000000") : toCss(stored);
  const commit = (event: { currentTarget: { value: string } }) => {
    dismissError();
    field.handleChange(event.currentTarget.value);
  };

  return (
    <>
      {/* h-8：同行字段报错变高时，网格默认的 stretch 会把这个盒子一起撑高，边框也跟着变高。 */}
      <div className="group relative h-8">
        <s-clickable
          commandFor={popoverId}
          type="button"
          blockSize="32px"
          inlineSize="100%"
          paddingInline="small"
          border="none"
          borderRadius="base"
          disabled={disabled}
          accessibilityLabel={t("OpenColorPicker", { label })}
        >
          <s-stack direction="inline" gap="small-300" alignItems="center" blockSize="100%">
            {themeOnly ? (
              <s-icon type="database" color="subdued" />
            ) : (
              // 棋盘格垫在颜色下面，半透明时看得出来。
              <span
                style={{
                  display: "block",
                  flexShrink: 0,
                  inlineSize: "20px",
                  blockSize: "20px",
                  borderRadius: "4px",
                  boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.08) inset",
                  backgroundImage: `linear-gradient(${value}, ${value}), repeating-conic-gradient(#fff 0% 25%, #f7f7f7 0% 50%)`,
                  backgroundSize: "auto, 8px 8px",
                }}
              />
            )}
            <s-text color="subdued">{themeOnly ? t("ThemeColor") : value}</s-text>
          </s-stack>
        </s-clickable>
        <span className="pointer-events-none absolute inset-0 rounded-lg shadow-[inset_0_0_0_0.66px_#8A8A8A] group-hover:shadow-[inset_0_0_0_0.66px_#616161]" />
      </div>
      <s-popover id={popoverId}>
        <s-box padding="small">
          <s-stack gap="small-200">
            <s-color-picker
              alpha
              name={field.name}
              value={value}
              onInput={commit}
              onChange={(event) => {
                commit(event);
                handleBlur();
              }}
            />
            <s-grid gridTemplateColumns="auto 1fr" gap="small-200" alignItems="center">
              <s-button
                variant="secondary"
                icon="database"
                disabled={disabled}
                accessibilityLabel={t("ResetToDefaultColor", { label })}
                onClick={() => {
                  // 有预设色号的行，默认就是那个色号，恢复要提交它；没预设才回主题色（空值）。
                  field.handleChange(preset ?? "");
                  handleBlur();
                }}
              />
              <s-text-field
                label={label}
                labelAccessibilityVisibility="exclusive"
                name={field.name}
                // 主题色时不显示垫底的十六进制值，用占位符说明这一行还是主题色。
                value={themeOnly ? "" : value}
                placeholder={t("ThemeColor")}
                error={error}
                disabled={disabled}
                onFocus={dismissError}
                onInput={commit}
                onBlur={handleBlur}
              />
            </s-grid>
          </s-stack>
        </s-box>
      </s-popover>
    </>
  );
}
